import { RefObject, useCallback } from 'react';
import { useSonTranslation } from '../../../../translation/useSonTranslation';

function isValidDate(date: Date) {
  return !Number.isNaN(date.getTime());
}

export function useDateRangeValidation(
  startRef: RefObject<HTMLInputElement | null>,
  endRef: RefObject<HTMLInputElement | null>,
  dateRangeTypeDependentValidation?: (
    startRef: RefObject<HTMLInputElement | null>,
    endRef: RefObject<HTMLInputElement | null>
  ) => void
) {
  const { t } = useSonTranslation('FormDateRange');

  return useCallback(() => {
    // Reset custom validation messages before evaluation.
    // This prevents issues when one of two fields is cleared and the DateRange is then revalidated.
    startRef.current?.setCustomValidity('');
    endRef.current?.setCustomValidity('');

    if (startRef.current?.valueAsNumber && endRef.current?.valueAsNumber) {
      const startDate = new Date(startRef.current.valueAsNumber);
      const endDate = new Date(endRef.current.valueAsNumber);

      if (!isValidDate(startDate)) {
        startRef.current?.setCustomValidity(t('startDateInvalid'));
        return;
      }

      if (!isValidDate(endDate)) {
        endRef.current?.setCustomValidity(t('endDateInvalid'));
        return;
      }

      if (startDate < endDate) {
        startRef.current?.setCustomValidity('');
        endRef.current?.setCustomValidity('');
        return;
      }

      if (startDate > endDate) {
        startRef.current?.setCustomValidity(t('startDateBeforeEndDate'));
        return;
      }

      if (typeof dateRangeTypeDependentValidation === 'function') {
        dateRangeTypeDependentValidation(startRef, endRef);
      }
    }
  }, [endRef, startRef, t]);
}
